import { Card, Flex, Popover, Space, Tag, theme } from 'antd';
import { FC } from 'react';
import { ArrowRightShort, CheckCircleFill, ExclamationCircleFill } from 'react-bootstrap-icons';
import { useParams } from 'react-router-dom';

import { AppCardList, DomainPunycode, MainDomainIcon, RegluingIcon } from '@/common/components';
import { AppRoutes, BaseQueryParams } from '@/common/interfaces';
import { CommonContainerTitle } from '@/common/layouts';

import { DomainGoogleVerificationTokenLabel, DomainSchemeTag, DomainVisitors, useGetAllSiteDomainsQuery } from '@/modules/Domains';

import './SiteDomainsList.scss';

interface Props {
  siteId: string;
  currentPage: number;
  siteDomainsItems: BaseQueryParams<typeof useGetAllSiteDomainsQuery>['data']['items'];
  siteDomainsTotalItems: number;
  siteDomainsItemsPerPage: number;
  isLoading: boolean;
  isAutoRegluingsEnabled: boolean;
  handleChangePage: (page: number) => void;
  handleBack: () => void;
}

export const SiteDomainsListView: FC<Props> = ({
  siteId,
  currentPage,
  siteDomainsItems,
  siteDomainsTotalItems,
  siteDomainsItemsPerPage,
  isAutoRegluingsEnabled,
  isLoading,
  handleChangePage,
  handleBack
}) => {
  const { token } = theme.useToken();
  const domainId = useParams()['*'];

  return (
    <>
      <CommonContainerTitle
        onBack={ handleBack }
        extra={
          <Space>
            { isAutoRegluingsEnabled ? (
              <Tag style={{ paddingBlock: 2 }}>
                <CheckCircleFill color={ token.colorSuccess }/>
                &nbsp;Автоматична переклейка увімкнена
              </Tag>
            ) : (
              <Tag style={{ paddingBlock: 2 }}>
                <ExclamationCircleFill color={ token.colorInfo }/>
                &nbsp;Автоматичну переклейку не увімкнено
              </Tag>
            ) }
          </Space>
        }
      >
        Домени
      </CommonContainerTitle>

      <Card>
        <AppCardList
          headerTitle={ null }
          pagination={{
            current: currentPage,
            total: siteDomainsTotalItems,
            pageSize: siteDomainsItemsPerPage,
            disabled: isLoading,
            onChange: (page: number) => handleChangePage(page)
          }}
          selectedKey={ domainId }
          rowKey="id"
          titleKey="name"
          grid={{
            gutter: 16,
            xs: 1,
            sm: 1,
            md: 1,
            lg: 2,
            xl: 2,
            xxl: 2
          }}
          navigateTo={ (record) => ({ to: AppRoutes.SiteDomainsById.replace(':siteId', siteId).replace(':domainId', record.id) }) }
          list={ siteDomainsItems }
          titleRender={ (title, { isSitesMain }) => (
            <Space>
              { isSitesMain && <MainDomainIcon color={ token.colorPrimary }/> }
              <DomainPunycode domainName={ title }/>
            </Space>
          ) }
          classNames={ (record) => ({
            cardClassName: record.isDeployed || record.id === domainId ? '' : 'card-domain-undeployed'
          }) }
          content={ (record) => (
            <Flex justify="space-between" gap={ 8 } align="center" style={{ width: '100%' }}>
              <Space>
                <DomainSchemeTag
                  isDeployed={ record.isDeployed }
                  isHttps={ record.isHttps }
                  deployStatus={ record.deployStatus }
                  withSpinner
                  simple
                />

                { record.regluings.length > 0 && (
                  <Popover
                    title="Переклейки"
                    content={
                      <Space direction="vertical" align="start">
                        { record.regluings.map((regluing) => (
                          <Flex key={ regluing.id } align="center" justify="space-between" gap={ 4 }>
                            <strong style={{ fontSize: 12 }}>{ `(${ regluing.forVisitor === DomainVisitors.GoogleBot ? 'Google Bot' : 'Будь-хто' })` }</strong>
                            <DomainPunycode domainName={ record.name } withIcon={ false }/>
                            <ArrowRightShort/>
                            <DomainPunycode domainName={ regluing.targetDomain.name } withIcon={ false }/>
                          </Flex>
                        )) }
                      </Space>
                    }
                  >
                    <Tag color="geekblue"><RegluingIcon/>&#8203;</Tag>
                  </Popover>
                ) }
              </Space>

              {
                record.siteHaveSearchConsoleAccount && (
                  <DomainGoogleVerificationTokenLabel isGoogleVerificated={ record.isGoogleVerificated }/>
                )
              }
            </Flex>
          ) }
        />
      </Card>
    </>
  );
};
